import JSZip from "jszip";
import { ID_PREFIX_PAGE, ID_PREFIX_SECTION, PAGE_TYPES } from "../../../../../../common/constants";
import { _LoadMediaFile } from "../../../../../../common/server_utils.es6";
import { createBox } from "../../../../../../common/common_tools";
import { stepToBoxes } from "./stepToBoxes";

// aidea.chrome.recorder (the ZIP producer) is the schema master — it stamps
// guide.schemaVersion at export time. Bump this when Studio adds support for
// a newer guide.json shape. Legacy ZIPs with no schemaVersion are v1.
const SUPPORTED_GUIDE_SCHEMA_VERSION = 2;

/**
 * Throws if guide.schemaVersion is newer than this importer supports, or is
 * a non-numeric truthy value. Missing/null/undefined (legacy pre-versioning
 * ZIPs) is accepted as v1.
 *
 * Tolerant reader: some out-of-tree recorder builds stamp schemaVersion as a
 * semver string (e.g. "1.0.0") instead of a number. Producers are being
 * fixed, but old ZIPs with the string form persist in the wild — parse the
 * leading integer and treat it as that major version rather than hard-reject.
 */
export function assertSupportedSchemaVersion(guide) {
  const v = guide && guide.schemaVersion;
  if (v == null) return;
  const major = typeof v === "string" ? parseInt(v, 10) : v;
  if (
    typeof major === "number" &&
    Number.isFinite(major) &&
    major <= SUPPORTED_GUIDE_SCHEMA_VERSION
  )
    return;
  throw new Error(
    "This guide was created with a newer recorder (schema v" +
      v +
      "). Supported up to v" +
      SUPPORTED_GUIDE_SCHEMA_VERSION +
      " — update Aidea Studio."
  );
}

function uploadFile(file) {
  return new Promise((resolve, reject) => {
    _LoadMediaFile(
      file,
      (_name, _mime, fileUrl) => {
        if (fileUrl) resolve(fileUrl);
        else reject(new Error("upload returned no fileUrl"));
      },
      (status) => {
        if (typeof status === "string" && status.startsWith("error")) {
          reject(new Error(status));
        }
      },
      // 4. argüman errorCallback: verilmediğinde _LoadMediaFile hatayı hiç
      // raporlamıyor ve bu promise asla settle olmuyordu — 429 gibi bir hata
      // ZIP içe aktarmayı sonsuza kadar askıda bırakıyordu.
      (diagnostics) =>
        reject(
          new Error(
            (diagnostics && (diagnostics.userMessage || diagnostics.message)) || "upload failed"
          )
        )
    );
  });
}

// Probe audio duration via HTMLAudioElement. Returns a finite positive number
// in seconds, or a fallback when the file has no metadata (webm recordings
// without a known duration return Infinity from `.duration`).
function probeAudioDuration(url, fallback = 60) {
  return new Promise((resolve) => {
    try {
      const a = new Audio();
      let done = false;
      const finish = (val) => {
        if (done) return;
        done = true;
        if (Number.isFinite(val) && val > 0) resolve(val);
        else resolve(fallback);
      };
      a.addEventListener("loadedmetadata", () => finish(a.duration));
      a.addEventListener("error", () => finish(null));
      a.src = url;
      setTimeout(() => finish(a.duration), 4000);
    } catch (_) {
      resolve(fallback);
    }
  });
}

/**
 * Parse ZIP, upload all assets, return parsed guide + asset URL maps.
 */
export async function parseAndUploadZip(file, onProgress) {
  if (onProgress) onProgress({ stage: "parsing", pct: 0, label: "Reading ZIP…" });
  const zip = await JSZip.loadAsync(file);

  const guideFile = zip.file("guide.json");
  if (!guideFile) throw new Error("guide.json not found in ZIP");
  const guideText = await guideFile.async("string");
  const guide = JSON.parse(guideText);
  assertSupportedSchemaVersion(guide);

  const urls = { screenshots: {}, audio: {}, videos: {} };
  const tasks = [];

  for (const path of Object.keys(zip.files)) {
    const entry = zip.files[path];
    if (entry.dir) continue;
    if (path.startsWith("screenshots/")) {
      const id = path.replace(/^screenshots\//, "").replace(/\.(webp|png|jpg|jpeg)$/i, "");
      tasks.push({ type: "ss", path, id });
    } else if (path.startsWith("audio/")) {
      const id = path.replace(/^audio\//, "").replace(/\.(webm|mp3|ogg|wav|m4a)$/i, "");
      tasks.push({ type: "audio", path, id });
    } else if (path.startsWith("videos/")) {
      const id = path.replace(/^videos\//, "").replace(/\.(webm|mp4|mov)$/i, "");
      tasks.push({ type: "video", path, id });
    }
  }

  const mimeByExt = {
    webp: "image/webp",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webm_audio: "audio/webm",
    webm_video: "video/webm",
    mp4: "video/mp4",
    mov: "video/quicktime",
    mp3: "audio/mpeg",
    ogg: "audio/ogg",
    wav: "audio/wav",
    m4a: "audio/mp4",
  };

  const total = tasks.length;
  let done = 0;

  for (const task of tasks) {
    const entry = zip.files[task.path];
    const blob = await entry.async("blob");
    const name = task.path.split("/").pop();
    const ext = (name.split(".").pop() || "").toLowerCase();
    let mime;
    if (ext === "webm") {
      mime = task.type === "audio" ? "audio/webm" : "video/webm";
    } else {
      mime = mimeByExt[ext] || "application/octet-stream";
    }
    const fileObj = new File([blob], name, { type: mime });

    const fileUrl = await uploadFile(fileObj);

    if (task.type === "ss") urls.screenshots[task.id] = fileUrl;
    else if (task.type === "audio") {
      // Probe duration so scene-level audio can be registered with a real
      // endTime (webm lacking metadata gets a conservative fallback).
      const duration = await probeAudioDuration(fileUrl, 60);
      urls.audio[task.id] = { url: fileUrl, duration, mime, name };
    } else if (task.type === "video") urls.videos[task.id] = fileUrl;

    done++;
    if (onProgress) {
      onProgress({
        stage: "uploading",
        pct: Math.round((done / Math.max(total, 1)) * 100),
        label: "Uploading " + done + " / " + total,
      });
    }
  }

  if (onProgress) onProgress({ stage: "done", pct: 100, label: "Done" });
  return { guide, urls };
}

/**
 * Creates a section + one scene per step, each with appropriate boxes.
 *
 * NOTE: deps.addNavItem must be a Redux-dispatch-bound action creator (NOT the
 * CarouselButtons-wrapped onNavItemAdded, which clobbers the `parent` arg).
 * Signature:
 *   addNavItem(id, name, parent, type, position, background,
 *              customSize, hideTitles, hasContent, sortable_id, navItemSelected)
 *
 * @param {object} guide - parsed guide.json
 * @param {object} urls - url map from parseAndUploadZip
 * @param {object} deps - { addNavItem, onBoxAdded, boxes }
 * @returns {{ sectionId: string, sceneIds: string[] }}
 */
export function createScenesFromGuide(guide, urls, deps) {
  const {
    addNavItem,
    onBoxAdded,
    boxes,
    sectionPosition,
    addSound,
    updateViewToolbar,
    uploadFile: registerFile,
  } = deps;
  // Section inserted at sectionPosition (after currently-selected item, or end of list).
  // Each scene inserted right after the section, keeping the section → children order.
  const sectionPos = typeof sectionPosition === "number" ? sectionPosition : 0;

  const sectionId = ID_PREFIX_SECTION + Date.now();
  addNavItem(
    sectionId,
    guide.title || "Screen Training",
    0, // parent = root
    PAGE_TYPES.SECTION,
    sectionPos, // position in flat navItemsIds list
    "rgb(255,255,255)",
    0, // customSize
    true, // hideTitles — no on-canvas header for imported content
    false, // hasContent — sections are containers
    null, // sortable_id
    null // navItemSelected
  );

  const steps = [...(guide.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const sceneIds = [];

  // Pre-compute stepId → sceneId so GuideClick/GuideHotspot targetStepId values
  // (recorder-side step ids) can be rewritten to studio scene ids before boxes
  // are emitted.
  const stepIdToSceneId = {};
  const _baseTs = Date.now();
  steps.forEach((step, idx) => {
    stepIdToSceneId[step.id] = ID_PREFIX_PAGE + _baseTs + "_" + idx;
  });

  steps.forEach((step, idx) => {
    const sceneId = stepIdToSceneId[step.id];
    sceneIds.push(sceneId);

    // Scene name: prefer user-authored description, else "Step N". Never use browser pageTitle.
    const desc = typeof step.description === "string" ? step.description.trim() : "";
    const sceneName = desc
      ? desc.length > 40
        ? desc.slice(0, 40) + "…"
        : desc
      : "Step " + (idx + 1);
    addNavItem(
      sceneId,
      sceneName,
      sectionId, // parent = our section
      PAGE_TYPES.SLIDE,
      sectionPos + 1 + idx, // position: right after section, preserving order
      "rgb(255,255,255)",
      0, // customSize
      true, // hideTitles — no on-canvas header for imported scenes
      true, // hasContent = true for slides
      null, // sortable_id
      null // navItemSelected
    );

    // Imported screen-training scenes are Guide-driven: the plugin controls
    // completion, not a time/animation gate. Clear isMandatory so the visor's
    // "Please complete the current stage" modal doesn't fire when the user
    // navigates between scenes manually.
    if (typeof updateViewToolbar === "function") {
      updateViewToolbar(sceneId, { isMandatory: false, minTime: 0 });
    }

    const boxDefs = stepToBoxes(step, urls, sceneId, idx, {
      stepIdToSceneId,
      // Per-guide video frame + start URL for the browser-frame overlay on
      // video steps (guide.metadata.videoStyle.frame).
      videoStyle: guide.metadata && guide.metadata.videoStyle,
      startUrl: guide.metadata && guide.metadata.startUrl,
    });

    for (const b of boxDefs) {
      createBox(
        {
          id: b.id,
          parent: b.parent,
          container: b.container,
          page: b.page,
          position: b.position,
          width: b.width,
          height: b.height,
          name: b.name,
          url: b.url,
          initialState: b.initialState,
          isDefaultPlugin: true,
        },
        b.name,
        true,
        onBoxAdded,
        boxes
      );
    }

    // Per-step audio → attach as scene-level sound (soundList). We also
    // register the file in filesUploaded via uploadFile so the visor's Howl
    // infrastructure can find it at runtime (soundPlayer matches sound.file
    // against Howl._originalSrc, which comes from filesUploaded entries).
    const audioEntry = step && step.id && urls && urls.audio && urls.audio["step-" + step.id];
    if (audioEntry && typeof addSound === "function") {
      const audioUrl = typeof audioEntry === "string" ? audioEntry : audioEntry.url;
      const audioDuration =
        typeof audioEntry === "object" && Number.isFinite(audioEntry.duration)
          ? audioEntry.duration
          : 60;
      const audioMime = (typeof audioEntry === "object" && audioEntry.mime) || "audio/webm";
      const audioName = (typeof audioEntry === "object" && audioEntry.name) || "step-" + step.id;
      const fileId = "imported-audio-" + _baseTs + "-" + idx;

      if (typeof registerFile === "function") {
        registerFile(fileId, audioUrl, audioName, [], audioMime);
      }

      addSound(sceneId, {
        file: audioUrl,
        name: audioName,
        startTime: 0,
        endTime: audioDuration,
        volumeLevel: 1,
      });
    }
  });

  return { sectionId, sceneIds };
}
